import { useEffect, useRef, type RefObject } from 'react'
import ModalBackdrop from '../ModalBackdrop'
import JoinPolicyChoice from '../leagueJoinPolicy/JoinPolicyChoice'
import type { LeagueJoinPolicy, LeagueJoinRequest } from '../../types'

interface LeagueSettingsMenuProps {
  inviteCode?: string
  // The trigger that opened the menu — focus returns to it on close, however the menu closed.
  triggerRef: RefObject<HTMLButtonElement | null>
  onClose: () => void
  // Hands off to the page's confirm modal; the menu never deletes anything itself.
  onRequestDelete: () => void
  onToast: (message: string) => void
  // Who can join, and who is currently waiting. The requests list is empty for every
  // policy but `request`, and the section it drives isn't rendered at all when it is empty.
  joinPolicy: LeagueJoinPolicy
  onChangeJoinPolicy: (joinPolicy: LeagueJoinPolicy) => void
  joinRequests: LeagueJoinRequest[]
  onAcceptJoinRequest: (requestId: string) => void
  onDeclineJoinRequest: (requestId: string) => void
}

/**
 * Admin settings menu. Flat surface + scrim separation via ModalBackdrop, which also
 * gives press-outside dismissal. Delete is danger-tinted but never commits here — it hands off to
 * the page's confirm modal. Mounted only while open, so its focus wiring is mount/unmount scoped.
 *
 * Order is deliberate: anything waiting for an answer comes first, then the setting
 * that decides whether anything can wait, then the invite code, then delete.
 */
function LeagueSettingsMenu({
  inviteCode,
  triggerRef,
  onClose,
  onRequestDelete,
  onToast,
  joinPolicy,
  onChangeJoinPolicy,
  joinRequests,
  onAcceptJoinRequest,
  onDeclineJoinRequest,
}: LeagueSettingsMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Focus moves into the menu when it opens and returns to the trigger when it closes (however it
  // closed — Esc, press-outside, or an action). Press-outside itself is ModalBackdrop's job; Esc is
  // wired here because the backdrop doesn't own keyboard dismissal.
  useEffect(() => {
    const trigger = triggerRef.current
    menuRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      trigger?.focus()
    }
  }, [onClose, triggerRef])

  const handleCopyInviteCode = async () => {
    if (!inviteCode) return
    onClose()
    await navigator.clipboard.writeText(inviteCode)
    onToast('Invite code copied to clipboard.')
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        className="league-detail__settings-menu"
        role="dialog"
        aria-label="League settings"
        tabIndex={-1}
        ref={menuRef}
      >
        <p className="league-detail__settings-title label-eyebrow">
          League settings
        </p>

        {joinRequests.length > 0 && (
          <div className="league-detail__settings-section">
            <p className="league-detail__section-label label-plain">
              {`Join requests (${joinRequests.length})`}
            </p>
            <ul className="league-detail__requests">
              {joinRequests.map((request) => (
                <li className="league-detail__request" key={request.id}>
                  <span className="league-detail__request-name">{request.displayName}</span>
                  <span className="league-detail__request-actions">
                    <button
                      type="button"
                      className="league-detail__request-accept"
                      onClick={() => onAcceptJoinRequest(request.id)}
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      className="league-detail__request-decline"
                      onClick={() => onDeclineJoinRequest(request.id)}
                    >
                      Decline
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="league-detail__settings-section">
          <p className="league-detail__section-label label-plain">
            Who can join
          </p>
          <JoinPolicyChoice
            name="league-join-policy"
            value={joinPolicy}
            onChange={onChangeJoinPolicy}
          />
        </div>

        <div className="league-detail__settings-section">
          <p className="league-detail__section-label label-plain">
            Invite code
          </p>
          <div className="league-detail__invite-code-row">
            <span className="league-detail__invite-code-value">
              {inviteCode}
            </span>
            <button
              className="league-detail__copy-btn"
              onClick={handleCopyInviteCode}
            >
              Copy
            </button>
          </div>
        </div>
        <button
          type="button"
          className="league-detail__settings-danger"
          onClick={onRequestDelete}
        >
          Delete league
        </button>
      </div>
    </ModalBackdrop>
  )
}

export default LeagueSettingsMenu
